import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { heroBannerService, HeroBannerMediaType } from '../../services/hero-banner.service';
import {
  hasCustomOverlayText,
  resolveOverlayFont,
  resolveOverlayLineColor,
  resolveBannerLayout,
  scaledRem,
  OVERLAY_LINE1_BASE_REM,
  OVERLAY_LINE2_BASE_REM,
  DEFAULT_BANNER_MASCOT,
  DEFAULT_BANNER_VIDEO,
  DEFAULT_OVERLAY_LINE1_COLOR,
  DEFAULT_OVERLAY_LINE2_COLOR,
  type HeroBannerLayout,
} from '../../constants/heroBannerDefaults';
import { isCoAdminOrAdmin } from '../../utils/roles';
import './HeroBanner.css';

interface BannerAnnouncement {
  text: string;
  visible: boolean;
  scheduledFrom: string | null;
  scheduledUntil: string | null;
  updatedBy: string;
  updatedAt: string;
}

interface BannerMedia {
  url: string;
  type: HeroBannerMediaType;
}

function isoToLocalDatetimeInput(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, '0');
  const D = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${M}-${D}T${h}:${m}`;
}

function isWithinSchedule(scheduledFrom: string | null, scheduledUntil: string | null): boolean {
  const now = new Date();
  if (scheduledFrom && new Date(scheduledFrom) > now) return false;
  if (scheduledUntil && new Date(scheduledUntil) < now) return false;
  return true;
}

const HeroBannerMedia: React.FC<{
  media: BannerMedia | null;
  fallbackVideoSrc: string;
  configLoaded: boolean;
}> = ({ media, fallbackVideoSrc, configLoaded }) => {
  if (!configLoaded) return null;

  if (media?.type === 'image') {
    return (
      <img
        className="hero-celebration-media"
        src={media.url}
        alt=""
        aria-hidden
        decoding="async"
      />
    );
  }

  const videoSrc = media?.type === 'video' ? media.url : fallbackVideoSrc;

  return (
    <video
      className="hero-celebration-media"
      src={videoSrc}
      autoPlay
      loop
      muted
      playsInline
      preload="none"
      aria-hidden
    />
  );
};

interface BannerOverlay {
  line1: string | null;
  line2: string | null;
  fontFamily: string | null;
  line1Color: string | null;
  line2Color: string | null;
}

const HeroBanner: React.FC = () => {
  const { user } = useAuth();
  const [bannerAnn, setBannerAnn] = useState<BannerAnnouncement | null>(null);
  const [bannerMedia, setBannerMedia] = useState<BannerMedia | null>(null);
  const [mascotUrl, setMascotUrl] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<BannerOverlay>({
    line1: null,
    line2: null,
    fontFamily: null,
    line1Color: null,
    line2Color: null,
  });
  const [layout, setLayout] = useState<HeroBannerLayout>(resolveBannerLayout(null));
  const [bannerConfigLoaded, setBannerConfigLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editScheduledFrom, setEditScheduledFrom] = useState('');
  const [editScheduledUntil, setEditScheduledUntil] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = isCoAdminOrAdmin(user?.role);

  useEffect(() => {
    if (isEditing) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isEditing]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await heroBannerService.get();
        if (!cancelled) {
          setBannerAnn({
            text: data.text || '',
            visible: data.visible,
            scheduledFrom: data.scheduledFrom,
            scheduledUntil: data.scheduledUntil,
            updatedBy: data.updatedBy || '',
            updatedAt: data.updatedAt,
          });
          setBannerMedia(
            data.mediaUrl?.trim() && data.mediaType
              ? { url: data.mediaUrl.trim(), type: data.mediaType }
              : null,
          );
          setMascotUrl(data.mascotUrl?.trim() || null);
          setOverlay({
            line1: data.overlayLine1,
            line2: data.overlayLine2,
            fontFamily: data.overlayFontFamily,
            line1Color: data.overlayLine1Color,
            line2Color: data.overlayLine2Color,
          });
          setLayout(resolveBannerLayout(data));
          try {
            localStorage.removeItem('hero_banner_announcement');
          } catch {
            /* ignore */
          }
        }
      } catch {
        if (!cancelled) {
          setBannerAnn(null);
          setBannerMedia(null);
          setMascotUrl(null);
          setOverlay({ line1: null, line2: null, fontFamily: null, line1Color: null, line2Color: null });
          setLayout(resolveBannerLayout(null));
        }
      } finally {
        if (!cancelled) {
          setBannerConfigLoaded(true);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const scheduledFrom = editScheduledFrom ? new Date(editScheduledFrom).toISOString() : null;
      const scheduledUntil = editScheduledUntil ? new Date(editScheduledUntil).toISOString() : null;
      const data = await heroBannerService.update({ text: trimmed, visible: true, scheduledFrom, scheduledUntil });
      setBannerAnn({
        text: data.text || '',
        visible: data.visible,
        scheduledFrom: data.scheduledFrom,
        scheduledUntil: data.scheduledUntil,
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt,
      });
      setIsEditing(false);
    } catch (err: any) {
      const m = err.response?.data?.message;
      alert(Array.isArray(m) ? m.join(' ') : m || err.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async () => {
    if (!bannerAnn) return;
    try {
      const data = await heroBannerService.update({ visible: !bannerAnn.visible });
      setBannerAnn({
        text: data.text || '',
        visible: data.visible,
        scheduledFrom: data.scheduledFrom,
        scheduledUntil: data.scheduledUntil,
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt,
      });
    } catch (err: any) {
      const m = err.response?.data?.message;
      alert(Array.isArray(m) ? m.join(' ') : m || err.message || 'Failed to update visibility');
    }
  };

  const handleStartEdit = () => {
    setEditText(bannerAnn?.text || '');
    setEditScheduledFrom(bannerAnn?.scheduledFrom ? isoToLocalDatetimeInput(bannerAnn.scheduledFrom) : '');
    setEditScheduledUntil(bannerAnn?.scheduledUntil ? isoToLocalDatetimeInput(bannerAnn.scheduledUntil) : '');
    setIsEditing(true);
  };

  const handleCloseModal = () => {
    if (!saving) setIsEditing(false);
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const firstName = user?.fullName?.split(' ')[0] || 'there';
  const effectivelyVisible = !!(
    bannerAnn?.visible &&
    isWithinSchedule(bannerAnn.scheduledFrom, bannerAnn.scheduledUntil)
  );
  const hasNote = !!(effectivelyVisible && bannerAnn?.text);
  const showCard = layout.showAnnouncementCard && (isAdmin || hasNote);
  const hasSchedule = !!(bannerAnn?.scheduledFrom || bannerAnn?.scheduledUntil);
  const useCustomOverlayText = hasCustomOverlayText(overlay.line1, overlay.line2);
  const resolvedMascot = mascotUrl || DEFAULT_BANNER_MASCOT;
  const overlayFont = resolveOverlayFont(overlay.fontFamily);
  const overlayLine1Color = resolveOverlayLineColor(overlay.line1Color, DEFAULT_OVERLAY_LINE1_COLOR);
  const overlayLine2Color = resolveOverlayLineColor(overlay.line2Color, DEFAULT_OVERLAY_LINE2_COLOR);

  return (
    <>
      <div className="hero-banner">
        <div className='hero-celebration-bg' aria-hidden />
        <HeroBannerMedia
          media={bannerMedia}
          fallbackVideoSrc={DEFAULT_BANNER_VIDEO}
          configLoaded={bannerConfigLoaded}
        />

        {bannerConfigLoaded && layout.showOverlay && useCustomOverlayText && (
          <div
            className="hero-vesak-text-stack"
            style={{ left: `${layout.overlayPosX}%`, top: `${layout.overlayPosY}%`, fontFamily: overlayFont }}
          >
            {overlay.line1?.trim() && (
              <p
                className="hero-overlay-line hero-overlay-line--primary"
                style={{
                  color: overlayLine1Color,
                  fontSize: scaledRem(OVERLAY_LINE1_BASE_REM, layout.overlayLine1Scale),
                }}
              >
                {overlay.line1.trim()}
              </p>
            )}
            {overlay.line2?.trim() && (
              <p
                className="hero-overlay-line hero-overlay-line--secondary"
                style={{
                  color: overlayLine2Color,
                  fontSize: scaledRem(OVERLAY_LINE2_BASE_REM, layout.overlayLine2Scale),
                }}
              >
                {overlay.line2.trim()}
              </p>
            )}
          </div>
        )}

        {bannerConfigLoaded && layout.showMascot && (
          <div
            className="hero-mascot-wrapper"
            style={{
              left: `${layout.mascotPosX}%`,
              bottom: `${layout.mascotPosY}%`,
              transform: `translateX(-50%) scale(${layout.mascotScale / 100})`,
              transformOrigin: 'bottom center',
            }}
          >
            <img
              src={resolvedMascot}
              alt="Banner mascot"
              className="hero-mascot-img"
              width={200}
              height={200}
              decoding="async"
              fetchPriority="high"
            />
          </div>
        )}

        <div
          className="hero-left"
          style={{
            left: `${layout.announcementPosX}%`,
            bottom: `${layout.announcementPosY}%`,
          }}
        >
          {showCard && (
            <div className="hero-announcement-card">
              <div className="hero-ann-dept">HR Announcement</div>
              {hasNote && (
                <p className="hero-ann-subtext no-border">{bannerAnn!.text}</p>
              )}

              {isAdmin && (
                <div className="hero-ann-admin">
                  {hasSchedule && (
                    <p className="hero-ann-schedule-info">
                      {bannerAnn!.scheduledFrom && (
                        <span>From {new Date(bannerAnn!.scheduledFrom).toLocaleString()}</span>
                      )}
                      {bannerAnn!.scheduledFrom && bannerAnn!.scheduledUntil && <span> · </span>}
                      {bannerAnn!.scheduledUntil && (
                        <span>Until {new Date(bannerAnn!.scheduledUntil).toLocaleString()}</span>
                      )}
                    </p>
                  )}
                  <div className="hero-ann-action-row">
                    <button className="hero-ann-edit-btn" onClick={handleStartEdit}>
                      {bannerAnn?.text ? 'Edit note' : '+ Add note'}
                    </button>
                    {bannerAnn?.text && (
                      <button className="hero-ann-toggle-btn" onClick={handleToggleVisibility}>
                        {bannerAnn.visible ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="hero-greeting-section">
        <h2 className="hero-greeting">
          {getGreeting()}, {firstName} San ! 👋
        </h2>
        <p className="hero-subtitle">
          Here's what's happening at Toyotsu Lanka today:
        </p>
      </div>

      {isEditing && ReactDOM.createPortal(
        <div className="hero-ann-modal-overlay" onClick={handleCloseModal}>
          <div className="hero-ann-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hero-ann-modal-header">
              <h3 className="hero-ann-modal-title">HR Announcement</h3>
              <button className="hero-ann-modal-close" onClick={handleCloseModal} aria-label="Close">&#x2715;</button>
            </div>

            <div className="hero-ann-modal-body">
              <label className="hero-ann-modal-field-label">
                Announcement text
                <input
                  className="hero-ann-modal-input"
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder="Enter announcement..."
                  maxLength={120}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                  autoFocus
                />
                <span className="hero-ann-modal-char-count">{editText.length}/120</span>
              </label>

              <div className="hero-ann-modal-schedule">
                <span className="hero-ann-modal-schedule-title">Schedule (optional)</span>
                <div className="hero-ann-modal-schedule-fields">
                  <label className="hero-ann-modal-field-label">
                    Show from
                    <input
                      className="hero-ann-modal-datetime"
                      type="datetime-local"
                      value={editScheduledFrom}
                      onChange={(e) => setEditScheduledFrom(e.target.value)}
                    />
                  </label>
                  <label className="hero-ann-modal-field-label">
                    Until
                    <input
                      className="hero-ann-modal-datetime"
                      type="datetime-local"
                      value={editScheduledUntil}
                      onChange={(e) => setEditScheduledUntil(e.target.value)}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="hero-ann-modal-footer">
              <button className="hero-ann-modal-cancel-btn" onClick={handleCloseModal} disabled={saving}>
                Cancel
              </button>
              <button className="hero-ann-modal-save-btn" onClick={handleSave} disabled={saving || !editText.trim()}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </>
  );
};

export default HeroBanner;
